import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  DataTable,
  Divider,
  IndexTable,
  IndexTableProps,
  InlineGrid,
  InlineStack,
  LegacyCard,
  Link,
  Modal,
  Page,
  Pagination,
  Select,
  SkeletonBodyText,
  Tabs,
  Text,
  TextField,
  Tooltip,
  useBreakpoints,
} from '@shopify/polaris';
import { InfoIcon, SearchIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import agent from '../../app/api/agent';
import {
  PaymentReconciliationDetail,
  PaymentReconciliationManualReview,
  PaymentReconciliationPageResult,
  PaymentReconciliationSummary,
} from '../../app/models/PaymentReconciliation';

const paymentModeBannerDismissalKey = 'rh-payment-reconciliation-mode-banner-dismissed';

const emptyPage: PaymentReconciliationPageResult = {
  items: [],
  page: 1,
  pageSize: 50,
  totalCount: 0,
  totalPages: 0,
};

const emptySummary: PaymentReconciliationSummary = {
  eligible: 0,
  needsAttention: 0,
  notReady: 0,
  excluded: 0,
  paidAutomatically: 0,
  paidManually: 0,
  failed: 0,
  archived: 0,
  lastEvaluatedAt: null,
  hasResults: false,
  automaticPaymentsEnabled: false,
  manualPaymentsEnabled: false,
  tolerance: 0.1,
};

const tabDefinitions = [
  { id: 'all', content: 'All results' },
  { id: 'eligible', content: 'Will mark paid' },
  { id: 'attention', content: 'Review required' },
  { id: 'not-ready', content: 'Not ready' },
  { id: 'excluded', content: 'Excluded' },
  { id: 'paid', content: 'Marked paid' },
  { id: 'failed', content: 'Update failures' },
  { id: 'archived', content: 'Archived' },
];

const headings = [
  { title: 'Shopify order' },
  { title: 'Order date' },
  { title: 'Shopify outstanding' },
  { title: 'Traverse invoice total' },
  { title: 'Traverse invoices closed' },
  { title: 'Result' },
  { title: '' },
];

const paidHeadings = [
  ...headings.slice(0, 2),
  { title: 'Marked paid on' },
  ...headings.slice(2),
];

const sortOptions = [
  { label: 'Order date: oldest first', value: 'date-asc' },
  { label: 'Order date: newest first', value: 'date-desc' },
  { label: 'Shopify outstanding: highest first', value: 'amount-desc' },
  { label: 'Shopify outstanding: lowest first', value: 'amount-asc' },
  { label: 'Order number: highest first', value: 'order-desc' },
  { label: 'Order number: lowest first', value: 'order-asc' },
];

const paidSortOptions = [
  { label: 'Marked paid: newest first', value: 'paid-desc' },
  { label: 'Marked paid: oldest first', value: 'paid-asc' },
  ...sortOptions,
];

const currency = (amount: number, currencyCode: string | null) => {
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currencyCode || 'CAD',
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'Eligible':
      return <Badge tone="success">Will mark paid</Badge>;
    case 'NeedsAttention':
      return <Badge tone="warning">Review required</Badge>;
    case 'NotReady':
      return <Badge tone="info">Not ready</Badge>;
    case 'PaidAutomatically':
    case 'PaidManually':
      return <Badge tone="success">Marked paid</Badge>;
    case 'Success':
      return <Badge tone="success">Success</Badge>;
    case 'Blocked':
      return <Badge tone="warning">Blocked</Badge>;
    case 'Failed':
      return <Badge tone="critical">Failed</Badge>;
    default:
      return <Badge tone="info">Excluded</Badge>;
  }
};

const actorLabel = (eventType: string, displayName: string | null) => {
  if (eventType === 'Evaluation') return 'the reconciliation service';
  if (displayName) return displayName;
  if (eventType === 'AutomaticMarkPaid') return 'Automatic reconciliation';
  return 'System';
};

const auditEventLabel = (eventType: string) => {
  switch (eventType) {
    case 'Evaluation':
      return 'Latest evaluation';
    case 'ManualMarkPaid':
      return 'Manual action';
    case 'AutomaticMarkPaid':
      return 'Automatic action';
    case 'ReconciliationTag':
      return 'Order tag';
    case 'Archived':
      return 'Archived';
    case 'ArchiveRestored':
      return 'Restored';
    case 'ArchiveReactivated':
      return 'Reactivated automatically';
    default:
      return eventType;
  }
};

const conciseApiError = (error: any, fallback: string) =>
  error?.response?.data?.message
  || error?.response?.data?.title
  || error?.response?.data?.Message
  || error?.message
  || fallback;

const humanizeTransactionValue = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const humanizeFulfillmentStatus = (value: string | null) => {
  switch (value?.toUpperCase()) {
    case 'FULFILLED':
      return 'Fulfilled';
    case 'PARTIALLY_FULFILLED':
      return 'Partially fulfilled';
    case 'UNFULFILLED':
      return 'Unfulfilled';
    default:
      return value || 'Unknown fulfillment';
  }
};

const formatJson = (value: string) => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const PaymentReconciliationPage = () => {
  const { smDown } = useBreakpoints();
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortValue, setSortValue] = useState('date-asc');
  // The paid tab has its own default without changing the sort used by other tabs.
  const [paidSortValue, setPaidSortValue] = useState('paid-desc');
  const isPaidView = tabDefinitions[selectedTab].id === 'paid';
  const activeSortValue = isPaidView ? paidSortValue : sortValue;
  const [page, setPage] = useState(1);
  const [pageResult, setPageResult] = useState<PaymentReconciliationPageResult>(emptyPage);
  const [summary, setSummary] = useState<PaymentReconciliationSummary>(emptySummary);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<PaymentReconciliationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [staffNote, setStaffNote] = useState('');
  const [manualActionLoading, setManualActionLoading] = useState(false);
  const [manualActionError, setManualActionError] = useState<string | null>(null);
  const [manualReview, setManualReview] = useState<PaymentReconciliationManualReview | null>(null);
  const [manualReviewLoading, setManualReviewLoading] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveNote, setArchiveNote] = useState('');
  const [archiveActionLoading, setArchiveActionLoading] = useState(false);
  const [archiveActionError, setArchiveActionError] = useState<string | null>(null);
  const [resultHelpOpen, setResultHelpOpen] = useState(false);
  // Remember this display preference across visits, regardless of payment mode.
  // Read before the first render so a dismissed banner does not flash during loading.
  const [paymentModeBannerDismissed, setPaymentModeBannerDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(paymentModeBannerDismissalKey) === 'true';
    } catch {
      return false;
    }
  });
  const dismissPaymentModeBanner = useCallback(() => {
    setPaymentModeBannerDismissed(true);
    try {
      window.localStorage.setItem(paymentModeBannerDismissalKey, 'true');
    } catch {
      // If browser storage is unavailable, dismissal still works for this visit.
    }
  }, []);
  const tabs = useMemo(() => [
    tabDefinitions[0],
    tabDefinitions[1],
    { ...tabDefinitions[2], content: `Review required (${summary.needsAttention.toLocaleString()})` },
    tabDefinitions[3],
    tabDefinitions[4],
    tabDefinitions[5],
    { ...tabDefinitions[6], content: `Update failures (${summary.failed.toLocaleString()})` },
    tabDefinitions[7],
  ], [summary.failed, summary.needsAttention]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  const loadResults = useCallback(async () => {
    try {
      const view = tabDefinitions[selectedTab].id;
      setPageResult(await agent.PaymentReconciliation.list({
        search: debouncedSearch || undefined,
        view: view === 'all' ? undefined : view,
        sort: activeSortValue,
        page,
        pageSize: 50,
      }));
    } catch {
      setError('Payment reconciliation results could not be loaded.');
    } finally {
      setIsLoading(false);
    }
  }, [activeSortValue, debouncedSearch, page, selectedTab]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await agent.PaymentReconciliation.summary());
    } catch {
      setError('Payment reconciliation summary could not be loaded.');
    }
  }, []);

  const refreshView = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      // Reload stored results only. Preserve the current tab, search, sort and page;
      // this must never trigger a sync, reconciliation evaluation or payment action.
      await Promise.all([loadResults(), loadSummary()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadResults, loadSummary]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadSummary();
      loadResults();
    }, 60_000);

    return () => window.clearInterval(interval);
  }, [loadResults, loadSummary]);

  const exportCsv = useCallback(async () => {
    setIsExporting(true);
    setError(null);
    try {
      const view = tabDefinitions[selectedTab].id;
      const response = await agent.PaymentReconciliation.exportCsv({
        search: debouncedSearch || undefined,
        view: view === 'all' ? undefined : view,
        sort: activeSortValue,
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const encodedFileName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const standardFileName = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1];
      const fileName = encodedFileName
        ? decodeURIComponent(encodedFileName)
        : standardFileName || 'payment-reconciliation.csv';
      const downloadUrl = window.URL.createObjectURL(response.data);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (exportError) {
      setError(conciseApiError(exportError, 'The CSV export could not be created.'));
    } finally {
      setIsExporting(false);
    }
  }, [activeSortValue, debouncedSearch, selectedTab]);

  const openDetails = useCallback(async (id: number) => {
    setDetailLoading(true);
    setManualActionError(null);
    try {
      setSelectedDetail(await agent.PaymentReconciliation.details(id));
    } catch {
      setError('The reconciliation details could not be loaded.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetails = useCallback(() => {
    setSelectedDetail(null);
    setDetailLoading(false);
    setConfirmationOpen(false);
    setStaffNote('');
    setManualActionError(null);
    setManualReview(null);
    setManualReviewLoading(false);
    setArchiveModalOpen(false);
    setArchiveNote('');
    setArchiveActionError(null);
  }, []);

  const beginArchive = useCallback(() => {
    setArchiveNote('');
    setArchiveActionError(null);
    setArchiveModalOpen(true);
  }, []);

  const archiveResult = useCallback(async () => {
    if (!selectedDetail) return;

    setArchiveActionLoading(true);
    setArchiveActionError(null);
    try {
      const result = await agent.PaymentReconciliation.archive(
        selectedDetail.result.shopifyOrderId,
        archiveNote
      );
      setNotice(`The reconciliation result for order ${result.shopifyOrderName || result.shopifyOrderId} was archived.`);
      closeDetails();
      await Promise.all([loadSummary(), loadResults()]);
    } catch (actionError) {
      setArchiveActionError(conciseApiError(
        actionError,
        'The reconciliation result could not be archived.'
      ));
    } finally {
      setArchiveActionLoading(false);
    }
  }, [archiveNote, closeDetails, loadResults, loadSummary, selectedDetail]);

  const restoreArchive = useCallback(async () => {
    if (!selectedDetail) return;

    setArchiveActionLoading(true);
    setArchiveActionError(null);
    try {
      const result = await agent.PaymentReconciliation.restore(
        selectedDetail.result.shopifyOrderId
      );
      setNotice(`The reconciliation result for order ${result.shopifyOrderName || result.shopifyOrderId} was restored to the active results.`);
      closeDetails();
      await Promise.all([loadSummary(), loadResults()]);
    } catch (actionError) {
      setArchiveActionError(conciseApiError(
        actionError,
        'The archived reconciliation result could not be restored.'
      ));
    } finally {
      setArchiveActionLoading(false);
    }
  }, [closeDetails, loadResults, loadSummary, selectedDetail]);

  const beginManualAction = useCallback(async () => {
    if (!selectedDetail) return;

    setStaffNote('');
    setManualActionError(null);
    setManualReview(null);
    setConfirmationOpen(true);
    setManualReviewLoading(true);
    try {
      setManualReview(await agent.PaymentReconciliation.manualReview(
        selectedDetail.result.shopifyOrderId
      ));
    } catch (reviewError) {
      setManualActionError(conciseApiError(
        reviewError,
        'Live Shopify payment activity could not be loaded. No payment action is available.'
      ));
    } finally {
      setManualReviewLoading(false);
    }
  }, [selectedDetail]);

  const markPaidManually = useCallback(async () => {
    if (!selectedDetail || !staffNote.trim()) return;

    setManualActionLoading(true);
    setManualActionError(null);
    try {
      const result = await agent.PaymentReconciliation.markPaidManually(
        selectedDetail.result.shopifyOrderId,
        staffNote.trim()
      );
      setNotice(`${result.shopifyOrderName || result.shopifyOrderId} recorded a manual Traverse payment and the action was logged.`);
      closeDetails();
      await Promise.all([loadSummary(), loadResults()]);
    } catch (actionError) {
      setManualActionError(conciseApiError(
        actionError,
        'Shopify did not record the manual Traverse payment. The attempt was logged.'
      ));
    } finally {
      setManualActionLoading(false);
    }
  }, [closeDetails, loadResults, loadSummary, selectedDetail, staffNote]);

  const rows = useMemo(() => pageResult.items.map((item, index) => {
    const relatedDocumentCount = item.traverseInvoices.length;
    const closedDocumentCount = item.traverseInvoices.filter(invoice => (
      invoice.paid && Math.abs(invoice.balanceDue) < 0.005
    )).length;
    const documentClosureSummary = relatedDocumentCount
      ? `${closedDocumentCount} / ${relatedDocumentCount}`
      : 'None';

    return (
      <IndexTable.Row id={String(item.id)} key={item.id} position={index}>
        <IndexTable.Cell>
          <BlockStack gap="050">
            <Link url={`shopify://admin/orders/${item.shopifyOrderId}`} target="_blank">
              {item.shopifyOrderName || item.shopifyOrderId}
            </Link>
            <Text as="span" variant="bodySm" tone="subdued">
              {humanizeFulfillmentStatus(item.shopifyFulfillmentStatus)}
            </Text>
          </BlockStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span">
            {item.shopifyOrderCreatedAt
              ? dayjs(item.shopifyOrderCreatedAt).format('MMM D, YYYY')
              : 'Not available'}
          </Text>
        </IndexTable.Cell>
        {isPaidView && <IndexTable.Cell>
          {item.lastActionAt ? (
            <BlockStack gap="050">
              <Text as="span">{dayjs(item.lastActionAt).format('MMM D, YYYY')}</Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {dayjs(item.lastActionAt).format('h:mm A')}
              </Text>
            </BlockStack>
          ) : <Text as="span">Not available</Text>}
        </IndexTable.Cell>}
        <IndexTable.Cell>
          <Text as="span">{currency(item.shopifyOutstandingAmount, item.currencyCode)}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span">{currency(item.traverseInvoiceTotal, item.currencyCode)}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span">{documentClosureSummary}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <div>{item.isArchived ? <Badge>Archived</Badge> : statusBadge(item.status)}</div>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Button variant="plain" onClick={() => openDetails(item.id)}>
            {item.manualActionAvailable ? 'Review' : 'View'}
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  }), [isPaidView, openDetails, pageResult.items]);

  const detail = selectedDetail?.result;
  const standardTraverseInvoices = detail?.traverseInvoices.filter(invoice => !invoice.isAdjustment) || [];
  const traverseAdjustments = detail?.traverseInvoices.filter(invoice => invoice.isAdjustment) || [];
  const hasTraverseAdjustments = traverseAdjustments.length > 0;
  const standardInvoiceTotal = standardTraverseInvoices.reduce((total, invoice) => total + invoice.total, 0);
  const adjustmentTotal = traverseAdjustments.reduce((total, invoice) => total + invoice.total, 0);
  const allStandardInvoicesPaid = standardTraverseInvoices.length > 0
    && standardTraverseInvoices.every(invoice => invoice.paid);
  const invoicedSalesOrderIds = new Set(
    (detail?.traverseInvoices || [])
      .map(invoice => invoice.traverseSalesOrderId?.trim().toLowerCase())
      .filter((salesOrderId): salesOrderId is string => Boolean(salesOrderId))
  );
  const relatedSalesOrderCount = detail?.traverseSalesOrderIds.length || 0;
  const invoicedSalesOrderCount = detail?.traverseSalesOrderIds.filter(salesOrderId => (
    invoicedSalesOrderIds.has(salesOrderId.trim().toLowerCase())
  )).length || 0;
  const invoiceRows = detail?.traverseInvoices.map(invoice => [
    invoice.traverseSalesOrderId || '',
    invoice.invoiceNumber || '',
    invoice.invoiceDate ? dayjs(invoice.invoiceDate).format('MMM D, YYYY') : '',
    currency(invoice.total, detail.currencyCode),
    currency(invoice.balanceDue, detail.currencyCode),
    invoice.isAdjustment ? 'Adjustment' : invoice.paid ? 'Paid' : 'Not paid',
  ]) || [];
  const uninvoicedSalesOrderRows = detail?.traverseSalesOrderIds
    .filter(salesOrderId => !invoicedSalesOrderIds.has(salesOrderId.trim().toLowerCase()))
    .map(salesOrderId => [
      salesOrderId,
      'Not available',
      'Not available',
      '',
      '',
      '',
    ]) || [];
  const traverseDocumentRows = [...invoiceRows, ...uninvoicedSalesOrderRows];
  const transactionRows = manualReview?.transactions.map(transaction => [
    transaction.processedAt ? dayjs(transaction.processedAt).format('MMM D, YYYY h:mm A') : 'Unknown',
    humanizeTransactionValue(transaction.kind),
    humanizeTransactionValue(transaction.status),
    transaction.gateway || 'Not supplied',
    currency(transaction.amount, transaction.currencyCode || manualReview.currencyCode),
  ]) || [];
  const latestEvaluationId = selectedDetail?.activity.find(activity => (
    activity.eventType === 'Evaluation'
  ))?.id;
  const visibleAuditActivity = selectedDetail?.activity.filter(activity => (
    activity.eventType !== 'Evaluation' || activity.id === latestEvaluationId
  )) || [];

  return (
    <Page
      title="Payment reconciliation"
      secondaryActions={[{
        content: 'Export CSV',
        onAction: exportCsv,
        loading: isExporting,
        disabled: !summary.hasResults || isLoading || isRefreshing,
      }, {
        content: 'Refresh',
        onAction: refreshView,
        loading: isRefreshing,
        disabled: isLoading || isRefreshing,
      }]}
    >
      <BlockStack gap="500">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}><p>{error}</p></Banner>}
        {notice && <Banner tone="success" onDismiss={() => setNotice(null)}><p>{notice}</p></Banner>}

        {!paymentModeBannerDismissed && <Banner
          tone={summary.automaticPaymentsEnabled ? 'warning' : 'info'}
          onDismiss={dismissPaymentModeBanner}
          title={summary.automaticPaymentsEnabled
            ? summary.manualPaymentsEnabled
              ? 'Automatic and manual payment updates are enabled'
              : 'Automatic payment updates are enabled'
            : summary.manualPaymentsEnabled
              ? 'Manual payment updates are enabled'
              : 'Preview mode: Shopify payment updates are disabled'}
        >
          <p>
            {summary.automaticPaymentsEnabled
              ? summary.manualPaymentsEnabled
                ? 'Eligible orders are revalidated against live Shopify immediately before they are marked paid. Manual payment actions remain available after live Shopify review.'
                : 'Eligible orders are revalidated against live Shopify immediately before they are marked paid. Manual payment updates are disabled.'
              : summary.manualPaymentsEnabled
                ? 'Automatic payment processing is disabled. Reconciliation runs automatically after successful Shopify order synchronization. Manual payment actions remain available after live Shopify review.'
                : 'Reconciliation runs automatically after successful Shopify order synchronization and records results without updating Shopify.'}
          </p>
        </Banner>}

        {!summary.hasResults && !isLoading && (
          <Banner tone="info" title="Preparing reconciliation results">
            <p>Results will appear after the first successful full Shopify order snapshot and background reconciliation evaluation.</p>
          </Banner>
        )}

        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center" wrap gap="300">
            <InlineStack blockAlign="center" gap="100">
              <Text as="h2" variant="headingSm">Current results</Text>
              <Tooltip content="About reconciliation results">
                <Button
                  variant="plain"
                  icon={InfoIcon}
                  accessibilityLabel="Explain reconciliation result categories"
                  onClick={() => setResultHelpOpen(true)}
                />
              </Tooltip>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              {summary.lastEvaluatedAt
                ? `Last evaluated ${dayjs(summary.lastEvaluatedAt).format('MMM D, YYYY [at] h:mm A')} · Matching tolerance: ${currency(summary.tolerance, 'CAD')}`
              : 'Not yet evaluated'}
            </Text>
          </InlineStack>
        </BlockStack>

        <BlockStack gap="300">
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onSelect={(index) => { setSelectedTab(index); setPage(1); }}
          />
          <div className="payment-reconciliation__filters">
            <TextField
              label="Search"
              labelHidden
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search by Shopify order, Traverse sales order, invoice, or reason"
              prefix={<SearchIcon />}
              clearButton
              onClearButtonClick={() => setSearchValue('')}
              autoComplete="off"
            />
            <Select
              label="Sort by"
              labelHidden
              options={isPaidView ? paidSortOptions : sortOptions}
              value={activeSortValue}
              onChange={(value) => {
                if (isPaidView) setPaidSortValue(value);
                else setSortValue(value);
                setPage(1);
              }}
            />
          </div>
        </BlockStack>

        <LegacyCard>
          {isLoading ? (
            <LegacyCard.Section><SkeletonBodyText lines={8} /></LegacyCard.Section>
          ) : pageResult.items.length === 0 ? (
            <LegacyCard.Section>
              <Box paddingBlock="800">
                <BlockStack gap="200" inlineAlign="center">
                  <Text as="h2" variant="headingMd">No results in this view</Text>
                  <Text as="p" tone="subdued">
                    {summary.hasResults ? 'Try adjusting your search or selecting another result type.' : 'Results will appear after the next successful background reconciliation evaluation.'}
                  </Text>
                </BlockStack>
              </Box>
            </LegacyCard.Section>
          ) : (
            <IndexTable
              condensed={smDown}
              resourceName={{ singular: 'reconciliation result', plural: 'reconciliation results' }}
              itemCount={pageResult.items.length}
              headings={(isPaidView ? paidHeadings : headings) as IndexTableProps['headings']}
              selectable={false}
            >
              {rows}
            </IndexTable>
          )}

          {pageResult.totalCount > 0 && (
            <LegacyCard.Section>
              <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                <Text as="p" tone="subdued">
                  {pageResult.totalCount.toLocaleString()} result(s), page {pageResult.page} of {pageResult.totalPages}
                </Text>
                <Pagination
                  hasPrevious={pageResult.page > 1}
                  onPrevious={() => setPage(current => Math.max(1, current - 1))}
                  hasNext={pageResult.page < pageResult.totalPages}
                  onNext={() => setPage(current => current + 1)}
                />
              </InlineStack>
            </LegacyCard.Section>
          )}
        </LegacyCard>
      </BlockStack>

      <Modal
        open={resultHelpOpen}
        onClose={() => setResultHelpOpen(false)}
        title="Understanding reconciliation results"
        primaryAction={{
          content: 'Close',
          onAction: () => setResultHelpOpen(false),
        }}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p">
              Results are recalculated automatically after successful Shopify order synchronization using the latest synchronized Shopify and Traverse data. Amount differences of $0.10 or less are treated as a match. The status banner shows whether automatic or manual Shopify payment updates are currently enabled.
            </Text>
            <BlockStack gap="300">
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">All results</Text>
                <Text as="p">Shows every current reconciliation result except archived results, which appear under Archived.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Will mark paid</Text>
                <Text as="p">The Shopify order is fully fulfilled, all related Traverse invoices are present and paid, the amounts match within tolerance, and no credits, returns, adjustments, or Shopify payment activity requiring review were found. Failed payment attempts, expired authorizations, and successfully voided authorizations are allowed when Shopify confirms no funds were received or refunded and nothing remains pending or capturable. It currently qualifies for automatic payment processing but has not yet been marked paid. Its data will be rechecked before any Shopify update.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Review required</Text>
                <Text as="p">The Traverse records are complete and paid, but Shopify contains payment activity requiring review, payment details are incomplete, or the amounts differ beyond tolerance. Active authorizations, pending transactions, captures, sales, refunds, and partial payments require review. A staff member must review the current Shopify data before deciding whether the full outstanding balance can be safely recorded as paid.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Not ready</Text>
                <Text as="p">Required Traverse records are missing or incomplete, or one or more invoices are unpaid or have a remaining balance. The order cannot be marked paid while this condition remains.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Excluded</Text>
                <Text as="p">The order is outside the supported payment workflow because it cannot be found in Shopify, is cancelled, is already paid or has no outstanding balance, is not fully fulfilled, cannot be marked paid in Shopify, or contains a Traverse credit, return, or adjustment. No payment action is available.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Marked paid</Text>
                <Text as="p">The app successfully recorded the full Shopify outstanding balance as paid, either automatically or through a manual staff action. The payment action is recorded in the audit history.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Update failures</Text>
                <Text as="p">An attempt to record a payment in Shopify failed because of a technical error. If an update is safely blocked because the order’s data changed, the order is reclassified instead of appearing here.</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm">Archived</Text>
                <Text as="p">A staff member archived this result. It is hidden from the active tabs and cannot be marked paid while archived. The system will continue checking for changes; if its reconciliation data changes, the result will automatically return to the active tabs for reevaluation.</Text>
              </BlockStack>
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={(selectedDetail !== null || detailLoading) && !confirmationOpen && !archiveModalOpen}
        onClose={closeDetails}
        title={detail ? `Reconciliation details for order ${detail.shopifyOrderName || detail.shopifyOrderId}` : 'Loading reconciliation details'}
        size="large"
        primaryAction={detail?.isArchived ? {
          content: 'Restore',
          onAction: restoreArchive,
          loading: archiveActionLoading,
        } : detail?.manualActionAvailable ? {
          content: 'Review Shopify payment activity',
          onAction: beginManualAction,
        } : detail?.archiveActionAvailable ? {
          content: 'Archive',
          onAction: beginArchive,
        } : undefined}
        secondaryActions={detail?.manualActionAvailable && detail.archiveActionAvailable ? [{
          content: 'Archive',
          onAction: beginArchive,
        }] : undefined}
      >
        {detailLoading || !selectedDetail || !detail ? (
          <Modal.Section><SkeletonBodyText lines={8} /></Modal.Section>
        ) : (
          <>
            <Modal.Section>
              <BlockStack gap="400">
                {archiveActionError && <Banner tone="critical"><p>{archiveActionError}</p></Banner>}
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">Shopify order</Text>
                  <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Order number</Text>
                      <Link url={`shopify://admin/orders/${detail.shopifyOrderId}`} target="_blank">
                        {detail.shopifyOrderName || detail.shopifyOrderId}
                      </Link>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Order date</Text>
                      <Text as="p" fontWeight="semibold">
                        {selectedDetail.shopifyOrderCreatedAt
                          ? dayjs(selectedDetail.shopifyOrderCreatedAt).format('MMM D, YYYY')
                          : 'Not available'}
                      </Text>
                    </BlockStack>
                  </InlineGrid>
                </BlockStack>

                <Divider />

                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">Reconciliation</Text>
                  <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Result</Text>
                      <div>{detail.isArchived ? <Badge>Archived</Badge> : statusBadge(detail.status)}</div>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Fulfillment</Text>
                      <Text as="p" fontWeight="semibold">{detail.shopifyFulfillmentStatus || 'Unknown'}</Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Shopify outstanding</Text>
                      <Text as="p" fontWeight="semibold">{currency(detail.shopifyOutstandingAmount, detail.currencyCode)}</Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Traverse invoice total</Text>
                      <Text as="p" fontWeight="semibold">
                        {currency(detail.traverseInvoiceTotal, detail.currencyCode)}
                      </Text>
                    </BlockStack>
                  </InlineGrid>
                </BlockStack>

                <Banner tone={detail.status === 'NeedsAttention' ? 'warning' : 'info'}>
                  <p>
                    {detail.status === 'Eligible'
                      ? 'All related Traverse invoices are present and paid, the order is fully fulfilled, and the totals match within tolerance. The order currently qualifies for automatic payment processing but has not yet been marked paid.'
                      : detail.decisionReason}
                  </p>
                </Banner>

                {detail.isArchived && (
                  <Banner tone="info" title="Archived from active results">
                    <BlockStack gap="100">
                      <p>
                        Archived {detail.archivedAt ? dayjs(detail.archivedAt).format('MMM D, YYYY h:mm:ss A') : ''}
                        {detail.archivedByDisplayName ? ` by ${detail.archivedByDisplayName}` : ''}.
                      </p>
                      {detail.archiveNote && <p>Note: {detail.archiveNote}</p>}
                      <p>
                        This order is still evaluated during future background reconciliation runs. It will return to the active results automatically if its reconciliation data changes.
                      </p>
                    </BlockStack>
                  </Banner>
                )}

                {hasTraverseAdjustments && (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">Traverse amount breakdown</Text>
                    <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">
                          {allStandardInvoicesPaid ? 'Paid invoice total' : 'Invoices before adjustments'}
                        </Text>
                        <Text as="p" fontWeight="semibold">
                          {currency(standardInvoiceTotal, detail.currencyCode)}
                        </Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">Credits and adjustments</Text>
                        <Text as="p" fontWeight="semibold">
                          {currency(adjustmentTotal, detail.currencyCode)}
                        </Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">Traverse net total</Text>
                        <Text as="p" fontWeight="semibold">
                          {currency(detail.traverseInvoiceTotal, detail.currencyCode)}
                        </Text>
                      </BlockStack>
                    </InlineGrid>
                  </BlockStack>
                )}

                {detail.lastAction && (
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingSm">Payment update</Text>
                    <Text as="p">
                      {detail.lastAction === 'Automatic' ? 'Marked paid automatically by the reconciliation service' : 'Marked paid manually'}
                      {detail.lastActionActor ? ` by ${detail.lastActionActor}` : ''}
                      {detail.lastActionAt ? ` on ${dayjs(detail.lastActionAt).format('MMM D, YYYY h:mm:ss A')}` : ''}.
                    </Text>
                    {detail.shopifyTransactionId && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        Shopify transaction: {detail.shopifyTransactionId}
                      </Text>
                    )}
                  </BlockStack>
                )}

                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Traverse sales orders and invoices</Text>
                  {relatedSalesOrderCount > 0 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Invoices were found for {invoicedSalesOrderCount} of {relatedSalesOrderCount} related Traverse sales orders.
                    </Text>
                  )}
                  {traverseDocumentRows.length ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'text']}
                      headings={['Sales order', 'Invoice', 'Invoice date', 'Total', 'Balance', 'Status']}
                      rows={traverseDocumentRows}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No related Traverse sales orders or invoices were found.</Text>
                  )}
                </BlockStack>
              </BlockStack>
            </Modal.Section>

            <Modal.Section>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">Audit activity</Text>
                {visibleAuditActivity.length === 0 ? (
                  <Text as="p" tone="subdued">No activity has been recorded.</Text>
                ) : visibleAuditActivity.map(activity => (
                  <div className="payment-reconciliation__activity-row" key={activity.id}>
                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                      <InlineStack gap="200" blockAlign="center" wrap>
                        <Text as="span" fontWeight="semibold">
                          {auditEventLabel(activity.eventType)}
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          by {actorLabel(activity.eventType, activity.actorDisplayName)}
                        </Text>
                      </InlineStack>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {dayjs(activity.occurredAt).format('MMM D, YYYY h:mm:ss A')}
                      </Text>
                    </InlineStack>
                    <Text as="p" variant="bodySm">{activity.reason}</Text>
                    {activity.eventType !== 'Evaluation' && activity.actorUserId && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        Shopify staff ID {activity.actorUserId}
                      </Text>
                    )}
                    <Text as="p" variant="bodySm" tone="subdued">
                      Shopify outstanding: {currency(activity.shopifyOutstandingAmount, detail.currencyCode)} · Traverse invoice total: {currency(activity.traverseInvoiceTotal, detail.currencyCode)} · Difference: {currency(activity.difference, detail.currencyCode)}
                    </Text>
                    {activity.staffNote && <Text as="p" variant="bodySm">Staff note: {activity.staffNote}</Text>}
                    {activity.errorMessage && <Text as="p" variant="bodySm" tone="critical">{activity.errorMessage}</Text>}
                    {activity.sourceSnapshotJson && (
                      <details className="fulfillment-audit__payload-disclosure">
                        <summary>Evaluation snapshot</summary>
                        <pre className="fulfillment-audit__payload">{formatJson(activity.sourceSnapshotJson)}</pre>
                      </details>
                    )}
                    {(activity.shopifyRequestJson || activity.shopifyResponseJson) && (
                      <details className="fulfillment-audit__payload-disclosure">
                        <summary>Shopify request and response</summary>
                        {activity.shopifyRequestJson && <pre className="fulfillment-audit__payload">{formatJson(activity.shopifyRequestJson)}</pre>}
                        {activity.shopifyResponseJson && <pre className="fulfillment-audit__payload">{formatJson(activity.shopifyResponseJson)}</pre>}
                      </details>
                    )}
                  </div>
                ))}
              </BlockStack>
            </Modal.Section>
          </>
        )}
      </Modal>

      <Modal
        open={archiveModalOpen && detail !== undefined}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveNote('');
          setArchiveActionError(null);
        }}
        title={`Archive result for order ${detail?.shopifyOrderName || detail?.shopifyOrderId || 'order'}?`}
        primaryAction={{
          content: 'Archive',
          onAction: archiveResult,
          loading: archiveActionLoading,
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => {
            setArchiveModalOpen(false);
            setArchiveNote('');
            setArchiveActionError(null);
          },
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {archiveActionError && <Banner tone="critical"><p>{archiveActionError}</p></Banner>}
            <Banner tone="info" title="Archived results cannot be marked paid">
              <p>
                This result will be hidden from the active tabs, but the system will continue checking it for changes. If its reconciliation data changes, it will automatically return to the active tabs for reevaluation.
              </p>
            </Banner>
            <TextField
              label="Archive note"
              value={archiveNote}
              onChange={setArchiveNote}
              multiline={4}
              maxLength={2000}
              showCharacterCount
              autoComplete="off"
              helpText="Optional. This note and your verified Shopify staff identity will be recorded in the audit history."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={confirmationOpen && detail !== undefined}
        onClose={() => {
          setConfirmationOpen(false);
          setManualActionError(null);
          setManualReview(null);
          setStaffNote('');
        }}
        title={`Mark order ${detail?.shopifyOrderName || detail?.shopifyOrderId || 'order'} paid in Shopify?`}
        primaryAction={{
          content: manualReview
            ? `Mark ${currency(manualReview.outstandingAmount, manualReview.currencyCode)} paid in Shopify`
            : 'Mark paid in Shopify',
          destructive: true,
          onAction: markPaidManually,
          loading: manualActionLoading,
          disabled: !staffNote.trim() || manualReviewLoading || !manualReview?.actionAllowed,
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => {
            setConfirmationOpen(false);
            setManualActionError(null);
            setManualReview(null);
            setStaffNote('');
          },
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {manualActionError && <Banner tone="critical"><p>{manualActionError}</p></Banner>}

            {manualReviewLoading ? (
              <SkeletonBodyText lines={10} />
            ) : manualReview && detail ? (
              <>
                {manualReview.blockReason ? (
                  <Banner tone="critical" title="Payment action blocked">
                    <p>{manualReview.blockReason}</p>
                  </Banner>
                ) : !manualReview.manualPaymentsEnabled ? (
                  <Banner tone="info" title="Manual payment updates are disabled">
                    <p>No manual payment can be recorded under the current settings. When manual updates are enabled, this action will record the full current Shopify outstanding balance as paid.</p>
                  </Banner>
                ) : (
                  <Banner tone="warning" title="This will update the live Shopify order">
                    <p>
                      Shopify will perform one final check, then record a manual Traverse payment for the full current outstanding balance.
                    </p>
                  </Banner>
                )}

                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                    <Text as="h3" variant="headingMd">Live Shopify payment activity</Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Checked {dayjs(manualReview.checkedAt).format('MMM D, YYYY h:mm:ss A')}
                    </Text>
                  </InlineStack>

                  <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Financial status</Text>
                      <Text as="p" fontWeight="semibold">
                        {manualReview.financialStatus ? humanizeTransactionValue(manualReview.financialStatus) : 'Unknown'}
                      </Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Outstanding</Text>
                      <Text as="p" fontWeight="semibold">
                        {currency(manualReview.outstandingAmount, manualReview.currencyCode)}
                      </Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Received</Text>
                      <Text as="p" fontWeight="semibold">
                        {currency(manualReview.totalReceived, manualReview.currencyCode)}
                      </Text>
                    </BlockStack>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">Refunded</Text>
                      <Text as="p" fontWeight="semibold">
                        {currency(manualReview.totalRefunded, manualReview.currencyCode)}
                      </Text>
                    </BlockStack>
                  </InlineGrid>

                  {transactionRows.length ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'numeric']}
                      headings={['Date', 'Type', 'Status', 'Gateway', 'Amount']}
                      rows={transactionRows}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No Shopify payment transactions were found.</Text>
                  )}

                  <Link url={`shopify://admin/orders/${manualReview.shopifyOrderId}`} target="_blank">
                    View order in Shopify
                  </Link>
                </BlockStack>

                <Divider />

                <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                  <BlockStack gap="050">
                    <Text as="p" variant="bodySm" tone="subdued">Traverse invoice total</Text>
                    <Text as="p" variant="headingMd">{currency(detail.traverseInvoiceTotal, detail.currencyCode)}</Text>
                  </BlockStack>
                  <BlockStack gap="050">
                    <Text as="p" variant="bodySm" tone="subdued">Difference</Text>
                    <Text as="p" fontWeight="semibold">{currency(detail.difference, detail.currencyCode)}</Text>
                  </BlockStack>
                </InlineGrid>

                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">Reason for review</Text>
                  <Text as="p">{detail.decisionReason}</Text>
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">Verified Shopify staff member</Text>
                  <Text as="p" fontWeight="semibold">{manualReview.staff.displayName}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Any manual payment action will be attributed to this staff member. Shopify staff ID: {manualReview.staff.shopifyStaffId}.
                  </Text>
                </BlockStack>

                <Divider />
                <TextField
                  label="Reason for marking paid"
                  value={staffNote}
                  onChange={setStaffNote}
                  multiline={4}
                  maxLength={2000}
                  showCharacterCount
                  autoComplete="off"
                  helpText="Required. This reason and your verified Shopify staff identity will be recorded in the audit history."
                />
                {(!manualReview.actionAllowed || !staffNote.trim()) && (
                  <Banner
                    tone={manualReview.blockReason ? 'warning' : 'info'}
                    title="Why Mark paid is unavailable"
                  >
                    <ul>
                      {!manualReview.manualPaymentsEnabled && (
                        <li>Manual payment updates are disabled under the current settings.</li>
                      )}
                      {manualReview.blockReason && <li>{manualReview.blockReason}</li>}
                      {!staffNote.trim() && <li>Enter a reason for marking the order paid.</li>}
                    </ul>
                  </Banner>
                )}
              </>
            ) : null}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default PaymentReconciliationPage;
